import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import "./i18n";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import * as Select from "@radix-ui/react-select";
import { CheckIcon, ChevronDownIcon } from "@radix-ui/react-icons";
import { SunIcon, MoonIcon } from "@radix-ui/react-icons";

function App() {
  const playBadges = {
    de: "/badges/badge-de.png",
    en: "/badges/badge-en.png",
    es: "/badges/badge-es.png",
    fr: "/badges/badge-fr.png",
    it: "/badges/badge-it.png",
    pt: "/badges/badge-pt.png",
    zh: "/badges/badge-zh.png",
    hi: "/badges/badge-hi.png",
  };

  const languages = [
    { code: "de", label: "Deutsch" },
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
    { code: "fr", label: "Français" },
    { code: "it", label: "Italiano" },
    { code: "pt", label: "Português" },
    { code: "hi", label: "हिन्दी" },
    { code: "zh", label: "中文" },
  ];

  function LanguageSelector({ currentLang, handleLanguageChange, mode }) {
    return (
      <Select.Root value={currentLang} onValueChange={handleLanguageChange}>
        {/* Trigger con gradiente dinámico según modo */}
        <Select.Trigger
          className={`
          inline-flex items-center justify-between px-4 py-2 rounded-lg shadow-lg transition-all relative z-10
          ${mode === "day"
              ? "bg-gradient-to-r from-day-gradientStart to-night-gradientEnd hover:from-night-gradientStart hover:to-day-gradientEnd"
              : "bg-gradient-to-r from-night-gradientStart to-day-gradientEnd hover:from-day-gradientStart hover:to-night-gradientEnd"
            }
        `}
        >
          <Select.Value
            placeholder="Idioma"
            className={`${mode === "day" ? "text-night-primary" : "text-white"} font-semibold`}
          />
          <Select.Icon>
            <ChevronDownIcon className={mode === "day" ? "text-day-textPrimary" : "text-white"} />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              style={{ position: "absolute", top: "100%", left: 0, zIndex: 1000 }}
            >
              <Select.Content
                className="rounded-lg shadow-2xl overflow-auto min-w-[160px] max-h-96"
                style={{
                  background: mode === "day"
                    ? "linear-gradient(to bottom right, #003c8f, #005b96)" // fondo azul noche
                    : "linear-gradient(to bottom right, #6EC6FF, #B2EBF2)"   // fondo azul día
                }}
                position="popper"
              >
                <Select.Viewport>
                  {languages.map((lang) => (
                    <motion.div key={lang.code} whileHover={{ scale: 1.03 }}>
                      <Select.Item
                        value={lang.code}
                        className={`
                        px-4 py-2 cursor-pointer flex items-center justify-between transition-colors
                        ${mode === "day"
                            ? "text-white hover:bg-night-secondary"   // alto contraste sobre fondo azul noche
                            : "text-day-textPrimary hover:bg-day-secondary" // alto contraste sobre fondo azul día
                          }
                      `}
                      >
                        <Select.ItemText>{lang.label}</Select.ItemText>
                        <Select.ItemIndicator>
                          <CheckIcon className={mode === "day" ? "text-white" : "text-day-textPrimary"} />
                        </Select.ItemIndicator>
                      </Select.Item>
                    </motion.div>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </motion.div>
          </AnimatePresence>
        </Select.Portal>
      </Select.Root>
    );
  }

  function ModeToggle({ mode, setMode }) {
    return (
      <motion.button
        onClick={() => setMode(mode === "day" ? "night" : "day")}
        className={`relative w-20 h-10 rounded-full p-1 transition-colors ${mode === "day" ? "bg-yellow-400" : "bg-gray-700"
          }`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95, rotate: 5 }} // <- ligera inclinación al hacer clic
      >
        <motion.div
          layout
          className="bg-white w-8 h-8 rounded-full shadow-md flex items-center justify-center"
          style={{ x: mode === "day" ? 0 : 40 }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }} // <- efecto “spring”
        >
          <motion.div
            key={mode}
            initial={{ rotate: -45, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: 45, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {mode === "day" ? (
              <SunIcon className="w-5 h-5 text-yellow-500" />
            ) : (
              <MoonIcon className="w-5 h-5 text-gray-700" />
            )}
          </motion.div>
        </motion.div>
      </motion.button>
    );
  }

  const [mode, setMode] = useState("day"); // day / night
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split("-")[0];
  const playBadge = playBadges[currentLang] || playBadges["es"];
  const [isLanguageLoaded, setIsLanguageLoaded] = useState(false);
  const [showSplash, setShowSplash] = useState(true); // animación inicial
  const particlesInit = async (engine) => {
    await loadSlim(engine);
  };

  useEffect(() => {
    if (i18n.language) {
      setIsLanguageLoaded(true);
      // Mostrar splash durante 2 segundos
      setTimeout(() => setShowSplash(false), 2000);
    }
  }, [i18n.language]);

  const colors =
    mode === "day"
      ? "from-day-gradientStart to-day-gradientEnd text-day-textPrimary"
      : "from-night-gradientStart to-night-gradientEnd text-night-textPrimary";

  const handleLanguageChange = (selectedLanguage) => {
    if (i18n && typeof i18n.changeLanguage === "function") {
      i18n.changeLanguage(selectedLanguage).catch((err) => {
        console.error("Error al cambiar el idioma:", err);
      });
    }
  };

  const updateMetaTags = useCallback(() => {
    const lang = i18n.language.split('-')[0] || "es";
    document.documentElement.lang = lang;

    const metaTitle = document.querySelector('meta[name="title"]');
    const metaDescription = document.querySelector('meta[name="description"]');

    document.title = t("pageTitle");

    if (metaTitle && metaDescription) {
      metaTitle.setAttribute("content", t("appTitle"));
      metaDescription.setAttribute("content", t("appDescription"));
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogTitle && ogDescription && ogImage) {
      ogTitle.setAttribute("content", t("appTitle"));
      ogDescription.setAttribute("content", t("appDescription"));
      ogImage.setAttribute("content", "https://transcryptvoice.com/logo_transparente.png");
    }

    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterTitle && twitterDescription) {
      twitterTitle.setAttribute("content", t("appTitle"));
      twitterDescription.setAttribute("content", t("appDescription"));
    }
  }, [i18n.language, t]);

  useEffect(() => {
    updateMetaTags();
  }, [updateMetaTags]);

  // Splash animado mientras se carga el idioma
  if (!isLanguageLoaded || showSplash) {
    return (
      <div
        className={`flex items-center justify-center min-h-screen bg-gradient-to-br ${colors} transition-colors duration-500`}
      >
        <motion.img
          src="/logo.png"
          alt="Logo"
          className="w-24 h-24 rounded-full"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2, ease: "easeInOut" }}
        />
      </div>
    );
  }

  // 🌟 App principal con partículas y contenido encima
  return (
    <div className={`relative bg-gradient-to-br ${colors} transition-colors duration-500`}>
      {/* Fondo de partículas */}
      <Particles
        id="tsparticles"
        init={particlesInit}
        className="absolute inset-0 z-0 pointer-events-none"
        options={{
          background: { color: { value: "transparent" } },
          fpsLimit: 60,
          particles: {
            number: { value: 40 },
            color: { value: mode === "day" ? "#64B5F6" : "#90CAF9" },
            links: {
              enable: true,
              color: mode === "day" ? "#0D47A1" : "#4FC3F7",
              distance: 150,
              opacity: 0.4,
              width: 1,
            },
            move: {
              enable: true,
              speed: 1.2,
              direction: "none",
              outModes: { default: "out" },
            },
            opacity: { value: 0.6 },
            shape: { type: "circle" },
            size: { value: { min: 1, max: 4 } },
          },
          detectRetina: true,
        }}
      />

      {/* Contenido principal encima */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen pt-12 pb-20">

        <div className="w-full flex justify-between items-center px-6 sm:px-12 pt-6 max-w-4xl">
          {isLanguageLoaded && (
            <LanguageSelector
              currentLang={i18n.language.split("-")[0]}
              handleLanguageChange={handleLanguageChange}
              mode={mode}
            />
          )}
          <ModeToggle mode={mode} setMode={setMode} />
        </div>

        {/* Título y descripción */}
        <motion.h1
          className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 animate-float flex flex-col sm:flex-row items-center text-center sm:text-left px-4"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <img src="/logo.png" alt={t("logoAlt")} className="w-16 h-16 rounded-full mr-2" />
          {t("appTitle")}
        </motion.h1>

        <h2 className="sr-only">{t("seoPhrase")}</h2>

        <p className="text-lg sm:text-xl font-medium mb-4 text-center sm:text-left">
          {t("slogan")}
        </p>

        <motion.p
          className="text-base sm:text-lg mb-8 max-w-md sm:max-w-2xl text-center leading-relaxed px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {t("appDescription")}
        </motion.p>

        <motion.a
          href="https://play.google.com/store/apps/details?id=com.toolongbro"
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.05 }}
          className="mt-4"
        >
          <img
            src={playBadge}
            alt="Disponible en Google Play"
            className="h-14 sm:h-16 md:h-18"
          />
        </motion.a>

        <div className="flex flex-col items-center mt-6 mb-6">
          <img
            src="/qr-code.png"
            alt="QR TranscryptVoice"
            className="w-64 h-64 sm:w-72 sm:h-72 md:w-80 md:h-80 rounded-lg shadow-lg bg-white p-4"
          />
        </div>

        {/* Features */}
        <motion.div
          className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl text-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="p-6 rounded-lg shadow-lg bg-white/20 backdrop-blur-md"
            >
              {t(`feature${i + 1}`)}
            </div>
          ))}
        </motion.div>

        {/* Footer */}
        <footer className="mt-12 w-full text-center p-4 text-sm">
          <div>
            <a
              href="mailto:support@transcryptvoice.com"
              className={`${mode === "day" ? "text-indigo-600 hover:underline" : "text-white hover:underline"
                }`}
            >
              {t("contactUs")}
            </a>
          </div>
          <div className="mt-2">
            <span className={`${mode === "day" ? "text-gray-600" : "text-white"}`}>
              {t("rightsReserved", { year: new Date().getFullYear() })}
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;